import { C, type AudienceId, type ProblemId } from "@bd/content";
import { WEEKS_PER_YEAR, yearOf, isHolidayShelf, priceIndex } from "./calendar";
import { comboFit, compile, fixMods, focusSimilarity, perfectCombo, priceCeiling, trueProblems, trueReplay, valueForMoney, sizeOf } from "./design";
import { addMarketGame, audienceCrowding, audienceSize, comboCopies, heat, marketGame, news, onHit, saturation, allAudiences } from "./market";
import { reviewGame, trendScore, type ReviewSet } from "./review";
import { BALANCE } from "./balance";
import { forecastRange, type Forecast } from "./production";
import { isUnlocked } from "./unlocks";
import { fixedRng } from "./rng";
import type { Company, GameState, Product, Project } from "./state";
import { hasTrait, traitBonus, furnFx, roomFx, talent, traitFx } from "./studio";
import { clamp, log, money, newId, rng, digits } from "./util";
import { perkFx } from "./perks";
import { modValue, salesMod } from "./mods";
import { lineDemand, lineFactor, onLineLaunch } from "./lines";
import { audienceMult } from "./ngplus";
import { dexp, dlog10, dpow } from "./dmath";
import { payShares } from "./cousins";

export const EVERGREEN_LINE = 0.74;

/**
 * The replay a game needs to become an evergreen in a given year: the bar rises as games get better, so evergreens
 * stay the rare few (about the top 8%) in every era instead of half of everything late on.
 */
export function evergreenLine(week: number): number {
  return Math.min(0.81, 0.722 + 0.0013 * Math.max(0, yearOf(week) - 1965));
}
export const CON_WEEKS = [30, 31]; // Dicefall Con, every summer

// ------------------------------------------------------------------ quality of a finished project

export interface ProjectQuality {
  design: number;
  art: number;
  writing: number;
}

export function projectQuality(p: Project, co: Company): ProjectQuality {
  const w = p.work;
  const mastery = clamp((co.mastery[p.design.mechanic] ?? 0) + perkFx(co, "mastery"));
  const theme = co.staff.reduce((s, x) => s + traitBonus(x, "theme", p.design.theme) * (x.projectId === p.id ? 1 : 0.3), 0)
    + co.staff.reduce((s, x) => s + (x.projectId === p.id ? traitBonus(x, "mechanic", p.design.mechanic) : 0), 0);
  const aud = co.staff.reduce((s, x) => s + (x.projectId === p.id ? traitBonus(x, "audience", p.design.audience) : 0), 0);
  return {
    design: clamp((w.prototype.skipped ? 0.15 : w.prototype.quality) + 0.15 * mastery + theme + aud),
    art: clamp(w.art.skipped ? 0.12 : w.art.quality + theme * 0.5),
    writing: clamp(w.rulebook.skipped ? 0.12 : w.rulebook.quality),
  };
}

export interface Evaluation {
  set: ReviewSet;
  problems: Record<ProblemId, number>;
  replay: number;
  novel: boolean;
  q: ProjectQuality;
}

/** Everything the reviewers would say if this project launched this week. */
export function evaluate(state: GameState, co: Company, p: Project, excludeGame?: string): Evaluation {
  const q = projectQuality(p, co);
  const d = p.design;
  const c = compile(d, fixMods(p.fixes), q.writing);
  const problems = trueProblems(d, c);
  const fit = comboFit(d);
  const focusSim = focusSimilarity(d);
  const novel = !(state.market.comboCount[`${d.mechanic}|${d.theme}`] > (excludeGame ? 1 : 0));
  const trend = trendScore(heat(state, "theme", d.theme), heat(state, "mechanic", d.mechanic), saturation(state, d.theme, excludeGame), comboCopies(state, d.mechanic, d.theme, excludeGame), novel);
  const value = valueForMoney(d, state.week, q.art, co.reputation);
  const set = reviewGame({ design: d, compiled: c, fit, focusSim, designQuality: q.design, problems, value, trend, hype: p.hype, artQuality: q.art, writingQuality: q.writing, week: state.week, followUp: p.kind !== "base" && p.kind !== "license" });
  const replay = trueReplay(d, c, { F: set.inputs.F, M: set.inputs.M, designQuality: q.design, mastery: co.mastery[d.mechanic] ?? 0, problems }) + perkFx(co, "replay") + (p.replayBonus ?? 0);
  return { set, problems, replay, novel, q };
}

// ------------------------------------------------------------------ launch

export function launch(state: GameState, co: Company, prod: Product, p: Project): void {
  const ev = evaluate(state, co, p);
  if (prod.kind === "base" || prod.kind === "edition" || prod.kind === "spinoff" || prod.kind === "license") {
    const sh = shelfSpace(state, co, prod.id);
    prod.shelf = sh.factor;
    const d0 = prod.design;
    const fee = listingFee(state, co, prod.id, (p.expectFY ?? 0) * d0.price * 0.45);
    if (fee > 0) {
      co.cash -= fee;
      co.deductible = (co.deductible ?? 0) + fee;
      log(state, "shelf_fee", `Shops charge ${money(fee)} to make room for ${prod.design.name}: it's game ${sh.recent + 1} from you this year, past the ${sh.slots} they take.`, co.id);
    }
    // once a year is enough
    if (sh.factor < 1 && co.shelfWarnedYear !== yearOf(state.week)) {
      co.shelfWarnedYear = yearOf(state.week);
      log(state, "shelf", `Shops already have ${sh.recent} new games from you this year, so ${prod.design.name} gets less shelf space. Fewer, bigger games sell better.`, co.id);
    }
  }
  const d = prod.design;
  prod.reviews = ev.set.reviews;
  prod.score = ev.set.score;
  prod.quality = ev.set.quality;
  prod.inputs = ev.set.inputs;
  prod.trueReplay = ev.replay;
  prod.problems = Object.fromEntries(Object.entries(ev.problems).filter(([, v]) => v > 0.05));
  prod.q = { design: ev.q.design, art: ev.q.art, writing: ev.q.writing, print: prod.orders[0]?.quality ?? 0.6 };
  // the box in your hands: a premium print lifts every review a little, a cheap one drags them down
  const printDelta = Math.round((prod.q.print - 0.6) * 0.6 * 10) / 10;
  if (printDelta !== 0) {
    for (const r of prod.reviews) {
      r.score = clamp(r.score + printDelta, 1, 10);
      r.lines = [...r.lines, { key: "print", label: "Print quality", points: printDelta, why: printDelta > 0 ? "Lovely components: thick card, crisp colours." : "Thin card and muddy colours let it down." }];
    }
    prod.score = clamp(prod.score + printDelta, 1, 10);
  }
  prod.hype = p.hype;
  prod.releaseWeek = state.week;
  prod.launched = true;
  if (p.crunch && !co.flags.includes("crunch_release")) co.flags.push("crunch_release");
  // the bigger the box, the bigger the bet: big releases land further from what anyone expected
  const risk = d.scope === "epic" ? 1.35 : d.scope === "big" ? 1.15 : 1;
  prod.reception = dexp(rng(state, "reception").normal(0, BALANCE.receptionSd * risk));
  prod.evergreen = ev.replay >= evergreenLine(state.week);
  const g = addMarketGame(state, { publisher: co.id, name: d.name, mechanic: d.mechanic, theme: d.theme, audience: d.audience, format: d.format, week: state.week, score: prod.score });
  prod.marketId = g.id;

  // the notebook learns the fits of a released combo
  co.notebook[`mt:${d.mechanic}:${d.theme}`] = ev.set.inputs.F >= 0 ? comboFit(d).mt : 0;
  co.notebook[`ma:${d.mechanic}:${d.audience}`] = C.mechanic[d.mechanic].audience[d.audience];
  co.notebook[`ta:${d.theme}:${d.audience}`] = C.theme[d.theme].audience[d.audience];
  co.mastery[d.mechanic] = clamp((co.mastery[d.mechanic] ?? 0) + 0.12 * (1 - (co.mastery[d.mechanic] ?? 0)));

  // research points: more from new combos and good games
  const rp = (3 + (ev.novel ? 3 : 0) + (prod.score >= 7 ? 2 : 0) + (prod.score >= 8.5 ? 2 : 0)) * Math.sqrt(sizeOf(d).work);
  co.research += rp;

  // reputation and fans
  co.reputation = clamp(co.reputation * 0.75 + prod.score * 10 * 0.25, 0, 100);
  co.stats.released += 1;
  if (prod.score >= 8) co.stats.hits += 1;
  if (prod.score < 5) co.stats.flops += 1;
  if (co.stats.firstReleaseWeek === null) co.stats.firstReleaseWeek = state.week;
  if (prod.score >= 8 && co.stats.first8Week === null) co.stats.first8Week = state.week;
  if (prod.evergreen && co.stats.firstEvergreenWeek === null) co.stats.firstEvergreenWeek = state.week;

  // everyone who put real time into it gains experience, even if they've already moved on to the next game
  // one point for shipping it, one for every 8 weeks you put in (up to 3), one more for a hit
  for (const s of co.staff) {
    const weeks = p.team?.[s.id] ?? (s.projectId === p.id ? 2 : 0);
    if (weeks >= 2) s.xp += (1 + Math.min(3, weeks / 8) + (prod.score >= 8 ? 1 : 0)) * (1 + perkFx(co, "xp") + traitFx(s, "xp")) * talent(s);
    if (s.projectId === p.id) s.projectId = undefined;
  }

  const combo = perfectCombo(d.mechanic, d.theme, d.audience);
  if (combo) {
    const found = co.unlocked.includes(`combo:${combo.name}`);
    if (!found) {
      co.unlocked.push(`combo:${combo.name}`);
      co.stats.combosFound += 1;
      prod.perfectCombo = `${co.name.split(" ")[0]}'s ${combo.name}`;
      log(state, "combo", `Secret perfect combo found: ${C.mechanic[d.mechanic].name} × ${C.theme[d.theme].name} for ${C.audience[d.audience].name}. The industry calls it "${prod.perfectCombo}".`, co.id);
    }
  }
  if (p.kind !== "base") {
    const parent = co.products.find((x) => x.id === p.parentId);
    if (parent && p.kind === "expansion") parent.expansions += 1;
  }
  deliverPreorders(state, co, prod, p);
  onLineLaunch(state, co, prod, p);
  log(state, "release", `${co.name} releases ${d.name} (${C.mechanic[d.mechanic].name} × ${C.theme[d.theme].name}): reviews average ${prod.score.toFixed(1)}.`, co.id);
  if (prod.score >= 8) {
    news(state, "hit", `${co.name}'s ${d.name} is a critical hit (${prod.score.toFixed(1)}).`);
    onHit(state, g, co.name);
  }
}

// ------------------------------------------------------------------ weekly sales

/** Your shops in other towns put your games in front of more people. */
export function storeReach(co: Company): number {
  return (co.stores ?? []).reduce((s, id) => s + (C.store[id]?.reach ?? 0), 0);
}

export function channelReach(state: GameState, co: Company, channels: string[], aud: AudienceId, week = state.week): number {
  let miss = 1;
  const woy = (week % WEEKS_PER_YEAR) + 1;
  for (const id of channels) {
    const ch = C.channel[id];
    if (!ch || !isUnlocked(co, `ch:${id}`)) continue;
    let r = ch.reach[aud];
    if (ch.seasonal) r = CON_WEEKS.includes(woy) ? r * 3 : 0;
    if (ch.fanScaled) r *= clamp(co.fans / fanMarket(week));
    r *= 1 + perkFx(co, "reach") + storeReach(co);
    miss *= 1 - clamp(r);
  }
  return 1 - miss;
}

let marginCache = new WeakMap<Company, { n: number; map: Map<string, number> }>();

/** Share of the shop price you keep across a product's channels (memoised until the company unlocks something new). */
export function channelMargin(co: Company, channels: string[], aud: AudienceId): number {
  // a shop front sells a share straight to fans, keeping almost the whole price
  const direct = Math.min(0.12, roomFx(co, "directSales") + furnFx(co, "directSales"));
  const m = channelMarginCached(co, channels, aud);
  return direct > 0 ? m * (1 - direct) + 0.9 * direct : m;
}

function channelMarginCached(co: Company, channels: string[], aud: AudienceId): number {
  let c = marginCache.get(co);
  if (!c || c.n !== co.unlocked.length) marginCache.set(co, (c = { n: co.unlocked.length, map: new Map() }));
  const key = `${channels.join(",")}|${aud}`;
  const hit = c.map.get(key);
  if (hit !== undefined) return hit;
  const v = channelMarginRaw(co, channels, aud);
  c.map.set(key, v);
  return v;
}

function channelMarginRaw(co: Company, channels: string[], aud: AudienceId): number {
  let w = 0;
  let m = 0;
  for (const id of channels) {
    const ch = C.channel[id];
    if (!ch || !isUnlocked(co, `ch:${id}`)) continue;
    const r = ch.reach[aud] * (ch.seasonal ? 0.15 : 1);
    w += r;
    m += r * ch.margin;
  }
  return w > 0 ? m / w : 0.4;
}

export function priceFactor(prod: Product, week: number, co: Company): number {
  const v = valueForMoney(prod.design, week, prod.q.art, co.reputation);
  let pi = clamp(1.3 - 0.6 * (prod.design.price / Math.max(0.01, v.fair)), 0.05, 1.3);
  if (v.overCeiling) pi *= 0.55;
  // buyers also have a budget: dearer boxes sell fewer copies, even when they're worth it
  const budget = BALANCE.budgetShare * priceCeiling(prod.design.audience, week, prod.design.scope);
  pi *= clamp(dpow(budget / Math.max(0.01, prod.design.price), BALANCE.priceElasticity), 0.3, 1.15);
  return pi;
}

export function interest(score: number): number {
  return BALANCE.interestMult * dpow(clamp(score / 10), BALANCE.interestPower);
}

export function decayWeeks(prod: Product): number {
  return C.audience[prod.design.audience].decayWeeks * (0.7 + 0.6 * prod.trueReplay);
}

/** The company's other releases for this audience in the last half year. */
export function ownRecent(co: Company, aud: string, exceptId: string, week: number): number {
  let n = 0;
  for (let i = co.products.length - 1; i >= 0; i--) {
    const p = co.products[i];
    if (!p.launched) continue;
    if (week - p.releaseWeek >= 26) break;
    if (p.id !== exceptId && p.design.audience === aud) n++;
  }
  return n;
}

let evergreenCache = new WeakMap<Company, { n: number; byAud: Map<string, number> }>();

/**
 * Forgets every remembered sales figure. Called at the start of each week and each online quarter, so the numbers
 * are always worked out from the world as it is now: a world kept in memory and one just loaded from a save agree.
 */
export function resetSalesCaches(): void {
  marginCache = new WeakMap();
  evergreenCache = new WeakMap();
}

/** How many live evergreens the company has in an audience (cached until the product list changes). */
export function evergreenCount(co: Company, aud: string): number {
  let c = evergreenCache.get(co);
  if (!c || c.n !== co.products.length) {
    c = { n: co.products.length, byAud: new Map() };
    for (const p of co.products) if (p.evergreen && !p.retired) c.byAud.set(p.design.audience, (c.byAud.get(p.design.audience) ?? 0) + 1);
    evergreenCache.set(co, c);
  }
  return c.byAud.get(aud) ?? 0;
}

/** Expected weekly demand (copies), without noise. Used by sales and by forecasts. */
/** New releases from you in the last year, and how much shelf space the next one would get. */
export function shelfSpace(state: GameState, co: Company, exceptId?: string): { recent: number; slots: number; factor: number } {
  const big = new Set(["base", "edition", "spinoff", "license"]);
  // games out this year, plus any already at the printer
  const recent = co.products.filter((p) => p.id !== exceptId && big.has(p.kind) && (p.launched ? state.week - p.releaseWeek < WEEKS_PER_YEAR : p.orders.length > 0)).length;
  const slots = BALANCE.shelfSlots[co.level] ?? 3;
  const factor = recent < slots ? 1 : 1 / (1 + BALANCE.shelfPenalty * (recent - slots + 1));
  return { recent, slots, factor };
}

/** How the time of year moves sales: the holiday shelf sells more, January less. */
export function seasonFactor(week: number): number {
  if (isHolidayShelf(week)) return BALANCE.holidayBoost;
  const w = (week % WEEKS_PER_YEAR) + 1;
  return w <= 6 ? BALANCE.januaryDip : 1;
}

/** Past the shelf limit, shops charge to list another new game: more for each game over. */
export function listingFee(state: GameState, co: Company, exceptId?: string, expectedRevenue = 0): number {
  // a garage gets less shelf space past its limit, but no bill: you're still learning
  if (co.level === "garage") return 0;
  const sh = shelfSpace(state, co, exceptId);
  const over = sh.recent - sh.slots + 1;
  // shops charge more to make room for a game they expect to sell well: a share of its first year, or a flat floor
  return over > 0 ? Math.round(Math.max(BALANCE.listingFee * priceIndex(state.week), expectedRevenue * BALANCE.listingShare) * over) : 0;
}

export function demand(state: GameState, co: Company, prod: Product, weeksSince: number, sold = prod.sold): number {
  // the week this sale happens in (a forecast looks ahead from today)
  const season = seasonFactor((prod.launched ? prod.releaseWeek : state.week) + weeksSince);
  return season * demandRaw(state, co, prod, weeksSince, sold);
}

function demandRaw(state: GameState, co: Company, prod: Product, weeksSince: number, sold: number): number {
  const d = prod.design;
  const A = audienceSize(d.audience, state.week) * BALANCE.salesScale * audienceMult(co, d.audience);
  const R = channelReach(state, co, prod.channels, d.audience);
  if (R <= 0) return 0;
  const pi = priceFactor(prod, state.week, co);
  // expansions, card sets and supplements sell to the line's owners
  const line = lineDemand(co, prod, weeksSince, sold);
  // a campaign for a follow-up reaches the owners who'd forgotten about the game
  if (line !== null) return Math.max(0, line * Math.min(1, R * 1.5) * pi * salesMod(co, prod) * (1 + 0.6 * prod.hype * dexp(-weeksSince / 6)));
  const crowd = 1 / (1 + audienceCrowding(state, d.audience) / BALANCE.crowding);
  const hype = 1 + 0.9 * prod.hype * dexp(-weeksSince / 6);
  const wom = clamp(1 + 0.18 * dlog10(1 + sold / 500) * (prod.trueReplay - 0.4) * 2, 0.85, 1.6);
  // fame: your fans against how big the market is now (20,000 fans was a lot in 1965; it isn't in 2020)
  const fk = fanMarket(state.week);
  // ...plus a slow climb for really big followings, so fans keep mattering when you're huge
  const fame = 1 + 0.5 * (co.fans / (co.fans + fk)) + 0.08 * dlog10(1 + co.fans / (fk * 15));
  const trendNow = clamp(1 + BALANCE.trendBoost * heat(state, "theme", d.theme) - saturation(state, d.theme, prod.marketId) * 1.2, 0.5, 1.7);
  const tau = decayWeeks(prod);
  const own = 1 / (1 + BALANCE.ownCannibal * ownRecent(co, d.audience, prod.id, state.week));
  // an edition sells to lapsed owners and a smaller pool of new buyers, not a whole new audience
  const size = sizeOf(d).sales * (prod.kind === "edition" ? 0.55 : 1);
  let base = size * (prod.shelf ?? 1) * own * A * interest(prod.score) * (prod.reception ?? 1) * R * pi * crowd * hype * wom * fame * trendNow * dexp(-weeksSince / tau) * salesMod(co, prod) * lineFactor(state, co, prod);
  if (prod.revivalWeek !== undefined && state.week >= prod.revivalWeek) {
    base += A * (prod.revivalI ?? 0.4) * R * pi * crowd * dexp(-(state.week - prod.revivalWeek) / tau);
  }
  let E = 0;
  if (prod.evergreen && !prod.retired) {
    const own = evergreenCount(co, d.audience);
    E = A * R * pi * crowd * BALANCE.evergreenShare * (1 + Math.max(0, prod.trueReplay - EVERGREEN_LINE) * 8) * (prod.score / 10) * dexp(-weeksSince / BALANCE.evergreenFade);
    // your evergreens share their audience's shelf, but only the top few compete
    E /= 1 + BALANCE.evergreenCannibal * Math.min(3, Math.max(0, own - 1));
    // a bigger box keeps selling a bit better for years, but the long tail doesn't scale with the box
    E *= Math.sqrt(size);
  }
  if (prod.clearance) base = Math.max(base, A * 0.02 * R) * 3;
  // expansions sell to a share of the base game's owners (see live products)
  return Math.max(0, base + E);
}

export function salesWeek(state: GameState, co: Company): number {
  const r = rng(state, "sales");
  let revenue = 0;
  for (const prod of co.products) {
    // deliveries
    for (const o of prod.orders) {
      if (o.arriveWeek === state.week) {
        let good = o.copies;
        if (o.misprint === "cards" || o.misprint === "pieces") {
          good = o.copies; // shipped with an errata insert or replacement parts: costs money and a little reputation
          co.cash -= Math.round(o.copies * o.unitCost * 0.08);
          co.reputation = clamp(co.reputation - 1.5, 0, 100);
          co.stats.misprints = (co.stats.misprints ?? 0) + 1;
          log(state, "misprint", `Misprint in ${prod.design.name}: ${o.misprint === "cards" ? "a card is printed wrong" : "pieces are missing"}. Fixing it costs ${money(o.copies * o.unitCost * 0.08)}.`, co.id);
        }
        if (o.misprint === "port") {
          co.stats.misprints = (co.stats.misprints ?? 0) + 1;
          log(state, "shipping", `${prod.design.name}'s shipment was stuck in port and arrived late.`, co.id);
        }
        prod.stock += good;
      }
    }
    prod.orders = prod.orders.filter((o) => o.arriveWeek > state.week);
    if (!prod.launched && prod.stock > 0 && prod.pending) {
      launch(state, co, prod, prod.pending);
      prod.pending = undefined;
    }
    if (!prod.launched) continue;
    if (prod.retired && prod.stock <= 0) continue;
    const weeksSince = state.week - prod.releaseWeek;
    // long-dead games: skip the maths (no evergreen floor, no revival, nothing on the shelf)
    if (!prod.evergreen && prod.revivalWeek === undefined && weeksSince > 8 * decayWeeks(prod) && (prod.stock <= 0 || weeksSince > 10 * WEEKS_PER_YEAR) && !(weeksSince >= 10 * WEEKS_PER_YEAR && weeksSince <= 20 * WEEKS_PER_YEAR && prod.trueReplay >= 0.6)) {
      if (prod.weekly.length) prod.weekly = [];
      continue;
    }
    // nothing on the shelf or on order a year after release: no sales to work out (sell-out effects only run in year one)
    const revivalCheck = state.week % WEEKS_PER_YEAR === 20 && weeksSince >= 10 * WEEKS_PER_YEAR;
    if (prod.stock <= 0 && prod.orders.length === 0 && weeksSince >= WEEKS_PER_YEAR && !revivalCheck) {
      if (prod.weekly.length) {
        prod.weekly.push(0);
        if (prod.weekly.length > 26 || prod.weekly.every((v) => v === 0)) prod.weekly.shift();
      }
      continue;
    }
    const want = demand(state, co, prod, weeksSince) * (1 + r.normal(0, 0.08));
    const units = Math.max(0, Math.min(prod.stock, Math.round(want)));
    const missed = Math.max(0, Math.round(want) - units);
    const price = prod.clearance ? prod.design.price * 0.35 : prod.design.price;
    const income = units * price * channelMargin(co, prod.channels, prod.design.audience) * modValue(co, "margin") * (1 - (prod.royaltyOut ?? 0));
    prod.stock -= units;
    prod.sold += units;
    prod.revenue += income;
    revenue += income;
    if (prod.shares) co.shareOut = (co.shareOut ?? 0) + payShares(state, prod, income);
    prod.weekly.push(units);
    if (prod.weekly.length > 26) prod.weekly.shift();
    if (units > (prod.weekly.length > 1 ? Math.max(...prod.weekly.slice(0, -1)) : 0)) prod.peakWeek = state.week;
    const mg = marketGame(state, prod.marketId);
    if (mg) mg.sold = prod.sold;
    // fans follow good games
    // the shop front and fan-facing displays grow the fan base faster
    co.fans = Math.max(0, co.fans + units * 0.06 * ((prod.score - 4.5) / 4) * (1 + roomFx(co, "fans") + furnFx(co, "fans") + annexFx(co, "fans")));
    // sold out: resellers, angry fans, and sometimes hype
    if (missed > 20 && prod.stock === 0 && weeksSince < 52) {
      prod.soldOutWeeks += 1;
      if (prod.soldOutWeeks === 1) log(state, "soldout", `${prod.design.name} has sold out. Resellers are charging double; fans want a reprint.`, co.id);
      if (prod.soldOutWeeks <= 3) prod.hype = clamp(prod.hype + 0.05);
      else co.reputation = clamp(co.reputation - 0.2, 0, 100);
    }
    if (prod.clearance !== undefined) {
      prod.clearance -= 1;
      if (prod.clearance <= 0 || prod.stock <= 0) {
        prod.clearance = undefined;
        if (prod.stock > 0) {
          log(state, "pulp", `The clearance of ${prod.design.name} is over: the last ${digits(prod.stock)} copies are pulped, freeing the warehouse.`, co.id);
          prod.stock = 0;
        }
        prod.retired = true;
      }
    }
    // toy chains return unsold stock when a game stalls
    if ((weeksSince === 13 || weeksSince === 26) && prod.channels.includes("toy_chains") && prod.stock > 0) {
      const peak = Math.max(1, ...prod.weekly);
      const rate = units / peak;
      if (rate < 0.25) {
        const shipped = prod.weekly.slice(-13).reduce((s, v) => s + v, 0);
        const back = Math.round(shipped * C.channel.toy_chains.returns * (1 - rate) * 0.6);
        if (back > 0) {
          prod.stock += back;
          prod.sold -= back;
          const refund = back * prod.design.price * C.channel.toy_chains.margin;
          co.cash -= refund;
          revenue -= refund;
          log(state, "returns", `Toy chains send back ${back} unsold copies of ${prod.design.name} (refund ${money(refund)}).`, co.id);
        }
      }
    }
    // cult revival: an old flop with high replay gets found by a streamer, 10-20 years later
    if (prod.revivalWeek === undefined && weeksSince >= 10 * WEEKS_PER_YEAR && weeksSince <= 20 * WEEKS_PER_YEAR && prod.trueReplay >= 0.6 && prod.score < 7.5 && state.week % WEEKS_PER_YEAR === 20) {
      const boost = co.staff.some((s) => s.role === "community") ? 1.6 : 1;
      const streamers = co.staff.some((s) => hasTrait(s, "streamer")) ? 1.3 : 1;
      if (r.chance(0.08 * boost * streamers)) {
        prod.revivalWeek = state.week;
        prod.revivalI = interest(prod.trueReplay * 10) * 0.8;
        prod.retired = false;
        news(state, "revival", `A ${yearOf(state.week) >= 2008 ? "streamer" : "magazine columnist"} rediscovers ${prod.design.name} from ${yearOf(prod.releaseWeek)}. Cult revival! Fans want copies.`);
        log(state, "revival", `Cult revival: ${prod.design.name} is selling again. Reprint before the moment passes.`, co.id);
      }
    }
  }
  return revenue;
}

export function newProduct(state: GameState, co: Company, p: Project): Product {
  return {
    id: newId(state, "p"), companyId: co.id, design: { ...p.design, focus: { ...p.design.focus }, parts: { ...p.design.parts } }, kind: p.kind, parentId: p.parentId,
    ...(p.pitch ? { royaltyOut: p.pitch.royalty, designer: p.pitch.designer } : {}),
    releaseWeek: -1, price: p.design.price, unitCost: 0, channels: defaultChannels(co, p.design.audience), reviews: [], score: 0, quality: 0,
    q: { design: 0, art: 0, writing: 0, print: 0 }, inputs: { F: 0, M: 0, P: 0, V: 0, T: 0, H: 0 }, problems: {}, trueReplay: 0, hype: 0, stock: 0,
    orders: [], sold: 0, revenue: 0, weekly: [], peakWeek: -1, evergreen: false, bargain: false, soldOutWeeks: 0, returnsDue: 0, expansions: 0,
    marketId: "", retired: false, launched: false, reception: 1,
  };
}

export function defaultChannels(co: Company, aud: AudienceId): string[] {
  return co.unlocked.filter((u) => u.startsWith("ch:")).map((u) => u.slice(3)).filter((id) => C.channel[id] && (C.channel[id].reach[aud] >= 0.1 || C.channel[id].best.includes(aud)));
}

/** Expected total copies over the first year, for forecasts before printing. */
export function expectedFirstYear(state: GameState, co: Company, p: Project): number {
  const ev = evaluate(state, co, p);
  const fake = newProduct(state, co, p);
  fake.score = ev.set.score;
  fake.trueReplay = ev.replay;
  fake.hype = p.hype;
  fake.q = { design: ev.q.design, art: ev.q.art, writing: ev.q.writing, print: 0.6 };
  fake.evergreen = ev.replay >= evergreenLine(state.week);
  if (p.kind === "base" || p.kind === "edition" || p.kind === "spinoff" || p.kind === "license") fake.shelf = shelfSpace(state, co).factor;
  let total = 0;
  for (let w = 0; w < WEEKS_PER_YEAR; w += 4) total += demand(state, co, fake, w, total) * 4;
  return Math.round(total);
}

/** The print-run forecast the player sees: a range with a confidence band, never one number, and never quite right. */
export function printForecast(state: GameState, co: Company, p: Project): Forecast & { expected: number } {
  const exp = expectedFirstYear(state, co, p);
  const f = forecastRange(exp, co);
  const err = fixedRng(`${p.id}:${Math.floor(state.week / 13)}`).normal(0, (1 - f.confidence) * 0.55);
  const k = dexp(err);
  return { low: Math.round(f.low * k), mid: Math.round(f.mid * k), high: Math.round(f.high * k), confidence: f.confidence, expected: exp };
}

/** What your extra buildings add (the landmark tower's fans, say). */
export function annexFx(co: Company, key: "fans" | "reputation"): number {
  let v = 0;
  for (const a of co.annexes ?? []) v += C.annex[a.kind]?.fx?.[key] ?? 0;
  return v;
}

/**
 * Launch day: pre-orders ship first. Ordinary pre-orders pay now; backers already paid. Short of stock, or late on a
 * crowdfunding promise, and people notice.
 */
function deliverPreorders(state: GameState, co: Company, prod: Product, p: Project): void {
  const want = p.preorders ?? 0;
  if (want <= 0) return;
  const n = Math.min(want, prod.stock);
  const backers = p.crowd?.funded ? p.crowd.backers : 0;
  const paid = Math.max(0, n - backers);
  prod.stock -= n;
  prod.sold += n;
  const income = Math.round(paid * prod.design.price * channelMargin(co, prod.channels, prod.design.audience));
  co.cash += income;
  if (n < want) {
    co.reputation = clamp(co.reputation - 3, 0, 100);
    log(state, "preorder", `${prod.design.name} ships ${digits(n)} of ${digits(want)} pre-orders: the rest wait for a reprint, and they're not happy.`, co.id);
  } else log(state, "preorder", `${prod.design.name} ships all ${digits(n)} pre-orders on launch day${paid ? ` (${money(income)})` : ""}.`, co.id);
  if (p.crowd?.funded && state.week > p.crowd.deadline) {
    co.reputation = clamp(co.reputation - 4, 0, 100);
    log(state, "preorder", `${prod.design.name} reached its backers late. They remember.`, co.id);
  }
}

/** The fan count that counts as "a real following" this year: 20,000 in 1965, growing with the whole market. */
const fanMarketCache = new Map<number, number>();
export function fanMarket(week: number): number {
  const y = yearOf(week);
  let v = fanMarketCache.get(y);
  if (v === undefined) {
    const total = (wk: number) => allAudiences().reduce((t, a) => t + audienceSize(a, wk), 0);
    // measured at the start of the year, so it's the same whichever week asks first (a fresh server starts mid-year)
    v = 20000 * Math.max(1, total((y - 1965) * WEEKS_PER_YEAR) / Math.max(1, total(0)));
    fanMarketCache.set(y, v);
  }
  return v;
}

/**
 * Why a game sells what it sells this week, as plain multipliers next to an average release: the reviews, the shops
 * that carry it, its price, the crowd it competes with, buzz, word of mouth, your fans, the trend, shelf space and age.
 */
export function salesWhy(state: GameState, co: Company, prod: Product): { label: string; value: string }[] {
  if (!prod.launched) return [];
  const d = prod.design;
  const w = state.week - prod.releaseWeek;
  const pct = (m: number) => `${m >= 1 ? "+" : "−"}${Math.round(Math.abs(m - 1) * 100)}%`;
  const fk = fanMarket(state.week);
  const lines: { label: string; value: string }[] = [];
  lines.push({ label: "Copies last week", value: digits((prod.weekly.at(-1) ?? 0)) });
  lines.push({ label: `Reviews (${prod.score.toFixed(1)})`, value: pct(interest(prod.score) / interest(7)) });
  lines.push({ label: "Shops that carry it", value: `${Math.round(channelReach(state, co, prod.channels, d.audience) * 100)}% of ${C.audience[d.audience].name.toLowerCase()} players` });
  lines.push({ label: "Price against what it's worth", value: pct(priceFactor(prod, state.week, co)) });
  lines.push({ label: "Competition for those players", value: pct(1 / (1 + audienceCrowding(state, d.audience) / BALANCE.crowding)) });
  if (prod.hype > 0.02 && w < 20) lines.push({ label: "Launch buzz", value: pct(1 + 0.9 * prod.hype * dexp(-w / 6)) });
  lines.push({ label: "Word of mouth", value: pct(clamp(1 + 0.18 * dlog10(1 + prod.sold / 500) * (prod.trueReplay - 0.4) * 2, 0.85, 1.6)) });
  lines.push({ label: "Your fans", value: pct(1 + 0.5 * (co.fans / (co.fans + fk)) + 0.08 * dlog10(1 + co.fans / (fk * 15))) });
  lines.push({ label: `${C.theme[d.theme].name} right now`, value: pct(clamp(1 + BALANCE.trendBoost * heat(state, "theme", d.theme) - saturation(state, d.theme, prod.marketId) * 1.2, 0.5, 1.7)) });
  if ((prod.shelf ?? 1) < 1) lines.push({ label: "Shelf space (too many new games this year)", value: pct(prod.shelf ?? 1) });
  lines.push({ label: `Age (${w} week${w === 1 ? "" : "s"})`, value: prod.evergreen ? "Evergreen: keeps selling" : pct(dexp(-w / decayWeeks(prod))) });
  if (seasonFactor(state.week) !== 1) lines.push({ label: seasonFactor(state.week) > 1 ? "Holiday shelf" : "January lull", value: pct(seasonFactor(state.week)) });
  if (prod.stock <= 0 && prod.orders.length === 0) lines.push({ label: "Out of stock", value: "Reprint to keep selling" });
  return lines;
}
